import { CartAddEvent } from '@theme/events';

/**
 * Complete Your Table — PDP set-builder.
 *
 * Reads the table data rendered by blocks/complete-your-table.liquid, tracks which
 * recommended pieces are in the cart, and recomputes a quiet progress bar + savings
 * preview + free-gift state. Adds pieces in place via the theme's own cart flow
 * (POST to cart_add_url with cart-items section ids, then dispatch CartAddEvent so the
 * drawer + cart bubble morph). The applied discount is handled server-side in Phase 3/6;
 * the saving shown here is a computed preview from the table's savings_tiers.
 */
class CompleteYourTable extends HTMLElement {
  connectedCallback() {
    this.scope = 'casual';
    this.archetype = this.dataset.archetype || 'dinnerware';
    this.casualTarget = toInt(this.dataset.casualTarget);
    this.hostingTarget = toInt(this.dataset.hostingTarget);
    this.currentProductId = this.dataset.currentProductId || '';
    this.currentRole = this.dataset.currentRole || '';
    this.currentTier = this.dataset.currentTier || 'casual';
    this.currentPrice = toInt(this.dataset.currentPrice);
    this.giftThreshold = this.dataset.giftThreshold || 'hosting';
    this.giftVariantId = this.dataset.giftVariantId || '';
    this.giftAutoAdd = this.dataset.giftAutoadd === 'true';
    this.showSavings = this.dataset.showSavings === 'true';
    this.tiers = safeParse(this.dataset.savingsTiers);
    this._giftBusy = false;

    this.refs = this.#collectRefs();
    this.tiles = Array.from(this.querySelectorAll('.cyt-tile'));
    this.cart = { items: [], currency: this.#currency() };

    this.#bindEvents();

    // Reflect cart state set elsewhere (one-by-one browsing, drawer edits).
    this.onCartUpdate = (event) => {
      if (event?.detail?.data?.source === 'complete-your-table') return;
      this.refresh();
    };
    document.addEventListener('cart:update', this.onCartUpdate);

    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.onCartUpdate);
  }

  #collectRefs() {
    const refs = {};
    for (const el of this.querySelectorAll('[ref]')) {
      const name = el.getAttribute('ref');
      if (!refs[name]) refs[name] = el;
    }
    return refs;
  }

  #bindEvents() {
    this.refs.toggleCasual?.addEventListener('click', () => this.setScope('casual'));
    this.refs.toggleHosting?.addEventListener('click', () => this.setScope('hosting'));

    this.addEventListener('click', (event) => {
      const button = event.target.closest('.cyt-tile__add');
      if (button && !button.disabled) this.handleAdd(button);
    });
  }

  setScope(scope) {
    this.scope = scope === 'hosting' ? 'hosting' : 'casual';
    this.refs.toggleCasual?.classList.toggle('is-active', this.scope === 'casual');
    this.refs.toggleHosting?.classList.toggle('is-active', this.scope === 'hosting');
    for (const tile of this.tiles) {
      if (tile.dataset.tier === 'hosting') tile.hidden = this.scope !== 'hosting';
    }
    this.recompute();
  }

  async handleAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;
    button.disabled = true;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const body = { items: [{ id: Number(variantId), quantity: 1 }] };
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add';

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (data.status) {
        // Server rejected (e.g. sold out); leave the button usable.
        button.disabled = false;
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.dataset.sectionId || 'complete-your-table', {
          source: 'complete-your-table',
          productId: button.closest('.cyt-tile')?.dataset.productId,
          variantId,
          sections: data.sections,
        })
      );

      await this.refresh();
    } catch (error) {
      console.error('[complete-your-table] add failed', error);
    } finally {
      button.disabled = false;
    }
  }

  async refresh() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      this.cart = await response.json();
    } catch (error) {
      this.cart = { items: [], currency: this.#currency() };
    }
    this.recompute();
  }

  recompute() {
    const target = this.scope === 'hosting' ? this.hostingTarget : this.casualTarget;
    const inScope = (tier) => this.scope === 'hosting' || tier === 'casual';

    const variantIds = new Set((this.cart.items || []).map((i) => String(i.variant_id)));
    const productIds = new Set((this.cart.items || []).map((i) => String(i.product_id)));

    const roles = new Set();
    let qualifyingTotal = 0;

    // The viewed piece is the anchor — it counts toward the set immediately.
    if (this.currentRole && inScope(this.currentTier)) {
      roles.add(this.currentRole);
      qualifyingTotal += this.currentPrice;
    }

    for (const tile of this.tiles) {
      const inCart = variantIds.has(tile.dataset.variantId) || productIds.has(tile.dataset.productId);
      tile.classList.toggle('is-in-set', inCart);
      const add = tile.querySelector('.cyt-tile__add');
      if (add) add.dataset.inCart = inCart ? 'true' : 'false';

      if (inCart && inScope(tile.dataset.tier) && !roles.has(tile.dataset.role)) {
        roles.add(tile.dataset.role);
        qualifyingTotal += toInt(tile.dataset.price);
      }
    }

    const count = Math.min(roles.size, target);
    const pct = this.#tierPercent(roles.size);

    // Progress
    if (this.refs.progressCount) this.refs.progressCount.textContent = `${count} of ${target}`;
    if (this.refs.fill) this.refs.fill.style.width = `${target ? Math.min((count / target) * 100, 100) : 0}%`;
    if (this.refs.track) {
      this.refs.track.setAttribute('aria-valuemax', String(target));
      this.refs.track.setAttribute('aria-valuenow', String(count));
    }

    // Savings preview
    const savings = Math.round((pct / 100) * qualifyingTotal);
    if (this.refs.savings) {
      this.refs.savings.textContent =
        this.showSavings && pct > 0 && savings > 0 ? `You’re saving ${this.#money(savings)}` : '';
    }

    // Next-tier nudge
    this.#renderNudge(roles.size);

    // Free gift
    this.#renderGift(roles.size);
    this.#syncGift(roles.size);
  }

  /**
   * Auto-add (or auto-remove) the free-gift line as the set crosses the threshold, so the
   * Function has a line to zero. Gated by gift_auto_add — keep OFF until the Function is live,
   * or the gift sits in the cart at full price. Tags the line with a _cyt_gift property so we
   * only ever remove the gift WE added (never a gift the shopper deliberately bought).
   */
  async #syncGift(roleCount) {
    if (!this.giftAutoAdd || !this.giftVariantId || this._giftBusy) return;
    const giftTarget = this.giftThreshold === 'casual' ? this.casualTarget : this.hostingTarget;
    const unlocked = roleCount >= giftTarget;
    const giftLine = (this.cart.items || []).find(
      (i) => String(i.variant_id) === String(this.giftVariantId) && i.properties && i.properties._cyt_gift
    );

    if (unlocked === !!giftLine) return; // already in the right state

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const root = window.Shopify?.routes?.root || '/';

    this._giftBusy = true;
    try {
      if (unlocked && !giftLine) {
        const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add';
        const body = { items: [{ id: Number(this.giftVariantId), quantity: 1, properties: { _cyt_gift: '1' } }] };
        if (sectionIds.length) {
          body.sections = sectionIds.join(',');
          body.sections_url = window.location.pathname;
        }
        await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: giftLine.key, quantity: 0, sections: sectionIds.join(',') }),
        });
      }
      this.dispatchEvent(new CartAddEvent({}, 'complete-your-table', { source: 'complete-your-table' }));
      await this.refresh();
    } catch (error) {
      console.error('[complete-your-table] gift sync failed', error);
    } finally {
      this._giftBusy = false;
    }
  }

  /** Highest tier % whose threshold is reached. Keys: numbers (role count) | 'casual' | 'hosting'. */
  #tierPercent(roleCount) {
    let best = 0;
    for (const [key, value] of Object.entries(this.tiers)) {
      const threshold = this.#threshold(key);
      if (threshold != null && roleCount >= threshold) best = Math.max(best, Number(value));
    }
    return best;
  }

  #threshold(key) {
    if (key === 'casual') return this.casualTarget;
    if (key === 'hosting') return this.hostingTarget;
    const n = Number(key);
    return Number.isFinite(n) ? n : null;
  }

  #renderNudge(roleCount) {
    const nudge = this.refs.nudge;
    if (!nudge) return;

    // Completion-focused nudge when discount messaging is off.
    if (!this.showSavings) {
      const target = this.scope === 'hosting' ? this.hostingTarget : this.casualTarget;
      const remaining = target - Math.min(roleCount, target);
      if (remaining > 0) {
        const item = this.archetype === 'gifting' ? 'sign' : 'piece';
        const whole =
          this.archetype === 'gifting'
            ? this.scope === 'hosting'
              ? 'circle'
              : 'pair'
            : this.archetype === 'barware'
            ? 'set'
            : 'table';
        nudge.textContent = `Add ${remaining} more ${remaining === 1 ? item : item + 's'} to complete your ${whole}.`;
        nudge.hidden = false;
      } else {
        nudge.hidden = true;
      }
      return;
    }

    let next = null;
    for (const [key, value] of Object.entries(this.tiers)) {
      const threshold = this.#threshold(key);
      if (threshold != null && threshold > roleCount) {
        if (!next || threshold < next.threshold) next = { threshold, pct: Number(value) };
      }
    }
    if (next) {
      const need = next.threshold - roleCount;
      const piece = need === 1 ? 'piece' : 'pieces';
      nudge.textContent = `Add ${need} more ${piece} to save ${next.pct}%.`;
      nudge.hidden = false;
    } else {
      nudge.hidden = true;
    }
  }

  #renderGift(roleCount) {
    const gift = this.refs.gift;
    if (!gift) return;
    const giftTarget = this.giftThreshold === 'casual' ? this.casualTarget : this.hostingTarget;
    const unlocked = roleCount >= giftTarget;
    gift.dataset.locked = unlocked ? 'false' : 'true';
    if (unlocked) {
      gift.textContent =
        this.archetype === 'gifting'
          ? 'Your seventh sign is on us — added free at checkout.'
          : 'Your finishing piece is included — free.';
    } else {
      gift.textContent =
        this.archetype === 'gifting'
          ? 'Complete your circle and your seventh sign is on us.'
          : 'Complete the full table and your finishing piece is our gift.';
    }
  }

  #currency() {
    return window.Shopify?.currency?.active || this.cart?.currency || 'USD';
  }

  #money(cents) {
    const currency = this.#currency();
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }
}

function toInt(value) {
  const n = parseInt(value ?? '0', 10);
  return Number.isFinite(n) ? n : 0;
}

function safeParse(value) {
  try {
    return JSON.parse(value || '{}');
  } catch (error) {
    return {};
  }
}

if (!customElements.get('complete-your-table')) {
  customElements.define('complete-your-table', CompleteYourTable);
}
